import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { DashboardWidgetWrapper } from '@/pages/organization/components/DashboardWidgetWrapper';
import { WidgetTable } from '@/components/WidgetTable';
import { TrendCell } from '@/pages/dashboard/tabs/ShadowAITab/ToolsTable';
import { UserInstalledToolsModal } from '@/pages/organization/components/UserInstalledToolsModal';
import { UserBrowserAIToolsModal } from '@/pages/organization/components/UserBrowserAIToolsModal';
import { useOrgTopShadowAiUsers } from '@/hooks/api/useShadowAiTools';
import type { ShadowAiTopUser } from '@kirin/api-client/contracts/shadow-ai';
import { ClickableCell } from '@/pages/organization/components/ClickableCell';

type ShadowAiUserSummary = {
  id: string;
  user: string;
  installedCount: number;
  browserCount: number;
  addedAt: string | null;
  extras?: Record<string, string>;
};

export function TopShadowAIUsers() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { data, isLoading, isError, refetch } = useOrgTopShadowAiUsers(
    organizationId,
    {
      limit: 10,
    }
  );

  const users: ShadowAiUserSummary[] =
    data?.users.map(
      (user: ShadowAiTopUser): ShadowAiUserSummary => ({
        id: user.userId,
        user: user.name ?? user.email ?? user.userId,
        installedCount: user.installedCount,
        browserCount: user.browserCount,
        addedAt: user.addedAt,
        extras: user.extras ?? undefined,
      })
    ) ?? [];

  const [selectedUser, setSelectedUser] = useState<ShadowAiUserSummary | null>(
    null
  );
  const [selectedBrowserUser, setSelectedBrowserUser] =
    useState<ShadowAiUserSummary | null>(null);

  const handleInstalledClick = (user: ShadowAiUserSummary) => {
    setSelectedUser(user);
  };

  const handleCloseToolsModal = () => {
    setSelectedUser(null);
  };

  const handleBrowserClick = (user: ShadowAiUserSummary) => {
    setSelectedBrowserUser(user);
  };

  const handleCloseBrowserModal = () => {
    setSelectedBrowserUser(null);
  };

  return (
    <DashboardWidgetWrapper
      title="Top Active Users"
      description="Shows up to 10 Users"
      className="col-span-12 xl:col-span-7"
      isLoading={isLoading}
      isError={isError}
      refetch={refetch}
    >
      <div className="flex flex-col gap-4 h-full">
        <WidgetTable<ShadowAiUserSummary>
          data={users}
          emptyState="No Data found"
          className="max-h-none"
          getRowId={user => user.id}
          columns={[
            {
              id: 'name',
              header: 'User',
              cell: user => (
                <span
                  className="font-medium truncate block max-w-[160px]"
                  title={user.user}
                >
                  {user.user}
                </span>
              ),
              className: 'min-w-[140px] max-w-[200px]',
            },
            {
              id: 'installed',
              header: 'Installed Tools',
              cell: user => (
                <ClickableCell
                  label={`${user.installedCount}`}
                  onClick={() => handleInstalledClick(user)}
                />
              ),
              className: 'min-w-[120px]',
            },
            {
              id: 'browser',
              header: 'Browser AI Tools',
              cell: user => (
                <ClickableCell
                  label={`${user.browserCount}`}
                  onClick={() => handleBrowserClick(user)}
                />
              ),
              className: 'min-w-[140px]',
            },
            {
              id: 'trend',
              header: 'Trend',
              cell: user => (
                <TrendCell
                  extras={user.extras ?? undefined}
                  addedAt={user.addedAt}
                />
              ),
              className: 'min-w-[110px]',
            },
          ]}
        />
      </div>
      <UserInstalledToolsModal
        open={selectedUser !== null}
        onClose={handleCloseToolsModal}
        user={selectedUser}
      />
      <UserBrowserAIToolsModal
        open={selectedBrowserUser !== null}
        onClose={handleCloseBrowserModal}
        user={selectedBrowserUser}
      />
    </DashboardWidgetWrapper>
  );
}
